import { useCategories } from "../../../store/categories";
import { useState, useMemo, useEffect } from "@wordpress/element";

/**
 * Single line item component
 */
const CategoryLineItem = ({
                              category,
                              selected,
                              hasSubs,
                              onSelect,
                              onDrillDown,
                          }) => {
    return (
        <div
            style={{ maxWidth: "100%" }}
            className={[
                "flex items-center justify-between py-3 px-2 -mx-2 rounded-lg transition",
                selected ? "bg-base-200/60" : "hover:bg-base-100",
            ].join(" ")}
        >
            <button
                type="button"
                onClick={() => (hasSubs ? onDrillDown(category) : onSelect(category))}
                className={[
                    "flex items-center justify-between flex-1 text-left text-sm pt-dark-blue transition cursor-pointer",
                    selected ? "font-black" : "hover:opacity-70",
                ].join(" ")}
            >
                <span className="flex items-center gap-2 min-w-0">
                    <span className="truncate">{category.name}</span>

                    {selected && (
                        <span className="badge badge-primary badge-sm shrink-0 flex items-center gap-1">
                            <i className="fa fa-check" />
                            Selected
                        </span>
                    )}
                </span>

                {hasSubs && (
                    <i className="fa fa-chevron-right ml-2 shrink-0 opacity-60" />
                )}
            </button>
        </div>
    );
};

export const CategoryFilter = ({ value, onChange }) => {
    const { tree, loading, fetchCategoryTree } = useCategories();
    const [path, setPath] = useState([]);

    useEffect(() => {
        fetchCategoryTree();
    }, [fetchCategoryTree]);

    const currentLevel = useMemo(() => {
        let current = tree;
        for (const segment of path) {
            const found = current.find((c) => c.id === segment.id);
            if (found?.subcategories) {
                current = found.subcategories;
            } else {
                return [];
            }
        }
        return current;
    }, [tree, path]);

    const currentCategory = path.length ? path[path.length - 1] : null;

    const isSelected = (cat) => {
        if (!cat || !value) return false;

        if (typeof value === "string" || typeof value === "number") {
            return value === cat.id || value === cat.slug;
        }

        return value.id === cat.id || value.slug === cat.slug;
    };

    const selectCategory = (category) => {
        // enforce single select
        onChange?.(category);
    };

    const drillDown = (category) => {
        selectCategory(category);
        setPath((prev) => [...prev, category]);
    };

    const goBack = () => {
        setPath((prev) => prev.slice(0, -1));
    };

    if (loading && (!tree || !tree.length)) {
        return <div className="py-4 text-center opacity-50">Loading categories...</div>;
    }

    return (
        <div className="flex flex-col">
            {/* Header */}
            {currentCategory && (
                <div className="flex items-center gap-2 mb-2">
                    <button
                        type="button"
                        onClick={goBack}
                        className="btn btn-ghost btn-xs px-1 h-auto min-h-0 pt-dark-blue hover:bg-transparent cursor-pointer"
                    >
                        <i className="fa fa-chevron-left" />
                    </button>

                    <button
                        type="button"
                        onClick={() => selectCategory(currentCategory)}
                        className="flex items-center gap-2 font-bold text-sm pt-dark-blue truncate cursor-pointer"
                        style={{ maxWidth: "100%" }}
                    >
                        <span className="truncate">{currentCategory.name}</span>

                        {isSelected(currentCategory) && (
                            <span className="badge badge-primary badge-sm shrink-0 flex items-center gap-1">
                                <i className="fa fa-check" />
                                Selected
                            </span>
                        )}
                    </button>
                </div>
            )}

            <div className="flex flex-col divide-y divide-base-200">
                {currentLevel.map((cat) => (
                    <CategoryLineItem
                        key={cat.id}
                        category={cat}
                        selected={isSelected(cat)}
                        hasSubs={!!cat.subcategories?.length}
                        onSelect={selectCategory}
                        onDrillDown={drillDown}
                    />
                ))}
            </div>
        </div>
    );
};